/**
 * Dialog for configuring remote settings
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfig } from "@/lib/config/config-context";
import { configureTenantsSDK } from "@/lib/harmony-sdk/config";
import { Globe, Key, AlertTriangle, Info } from "lucide-react";

interface RemoteConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RemoteConfigDialog({
  open,
  onOpenChange,
}: RemoteConfigDialogProps) {
  const { config, updateConfig, isLocalMode } = useConfig();

  const [tenantsBaseUrl, setTenantsBaseUrl] = useState(config.tenantsBaseUrl);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTenantsBaseUrl(config.tenantsBaseUrl);
  }, [config, open]);

  const handleSave = () => {
    setIsLoading(true);
    try {
      updateConfig({
        tenantsBaseUrl,
      });

      // Reconfigure SDK with new settings
      setTimeout(() => {
        configureTenantsSDK();
      }, 100);

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update remote config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setTenantsBaseUrl(config.tenantsBaseUrl);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Remote Settings</DialogTitle>
          <DialogDescription>
            Set up connection to remote Harmony API servers
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Remote Server URL */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Globe className="h-4 w-4" />
                Tenants Service URL
              </CardTitle>
              <CardDescription>
                The base URL of the remote Harmony tenants service
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="tenantsBaseUrl">Base URL</Label>
                <Input
                  id="tenantsBaseUrl"
                  value={tenantsBaseUrl}
                  onChange={(e) => {
                    setTenantsBaseUrl(e.target.value);
                  }}
                  placeholder={`${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/tenants`}
                  className="font-mono"
                />
              </div>
              <div className="text-muted-foreground text-sm">
                The full URL to the tenants service API. This should include the
                protocol and domain but not the /api/v1 path.
              </div>
            </CardContent>
          </Card>

          {/* Descope Project ID Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Key className="h-4 w-4" />
                Descope Project Configuration
              </CardTitle>
              <CardDescription>
                Authentication project settings for remote access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Descope Project ID Configuration
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    For security reasons, Descope project ID can only be
                    configured on the login page. This ensures proper
                    authentication context and prevents unauthorized project
                    switching.
                  </div>
                </div>
              </div>

              {config.descopeProjectId && (
                <div className="space-y-2">
                  <Label>Current Descope Project ID</Label>
                  <div className="bg-muted rounded border p-2 font-mono text-sm">
                    {config.descopeProjectId}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    To change this, log out and use the project configuration
                    option on the login page.
                  </div>
                </div>
              )}

              {!config.descopeProjectId && (
                <div className="text-muted-foreground text-sm">
                  No Descope project ID is currently configured. You can set
                  this on the login page.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Mode Warning */}
          {isLocalMode && (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
              <div className="space-y-1">
                <div className="font-medium text-yellow-800 dark:text-yellow-200">
                  Currently in Local Mode
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  These settings will only take effect when you switch to Remote
                  Mode. You can change the operation mode in the Mode
                  Configuration dialog.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
